import { axiosInstance } from '../config/axiosConfig';
import { API_ENDPOINTS } from '../endpoints/endpoints';
import {
  AdminActionStatus,
  AdminActionType,
  UserRole,
  UserStatus,
} from '@enum';

export interface AdminStats {
  totalListings: number;
  activeListings: number;
  rejectedListings: number;
  totalRevenue: number;
  todaySubmissions: number;
  recentActivity: RecentActivity[];
}

export interface RecentActivity {
  id: string;
  type: AdminActionType;
  listing: string;
  user: string;
  time: string;
  status: AdminActionStatus;
  createdAt: string;
  details: {
    listingId: string;
    userId: string;
    adminId: string;
    actionData?: string;
    reason?: string | null;
    adminNotes?: string;
  };
}

export interface AdminListingDetails {
  id: string;
  title: string;
  make: string;
  model: string;
  year: number;
  price: number;
  mileage: number;
  location: string;
  description: string;
  features: string[];
  images: Array<{
    id: string;
    url: string;
    isPrimary: boolean;
    alt: string;
  }>;
  user: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  paymentToken?: string;
  paymentAmount?: number;
  paymentDate?: string;
  status: string;
  submittedAt: string;
  createdAt: string;
  updatedAt: string;
  verifiedAt?: string;
  verifiedBy?: string;
  approvedAt?: string;
  approvedBy?: string;
  rejectionReason?: string;
  adminNotes?: string;
  timeline: TimelineItem[];
}

export interface TimelineItem {
  action: string;
  timestamp: string;
  description: string;
}

export interface PaginationResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface AdminUserItem {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  role: UserRole;
  status: UserStatus;
  createdAt: string;
  updatedAt: string;
  dealerId?: string | null;
  creditBalance?: number;
  deviceType?: string | null;
  signupMethod?: 'email' | 'google' | 'apple' | null;
}

export interface AdminUserDetail extends AdminUserItem {
  emailVerified: boolean;
  isActive: boolean;
  lastLogin?: string | null;
  signupMethod?: 'email' | 'google' | 'apple' | null;
}

export interface AdminUserListing {
  id: string;
  make: string;
  model: string;
  year: number;
  price: number;
  status: string;
  createdAt: string;
  image: string | null;
  thumbnailUrl: string | null;
}

export interface UpdateUserPayload {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  role?: string;
  status?: string;
}

// Credit Packages
export interface CreditPackage {
  id: string;
  label: string;
  credits: number;
  price: number;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
}

export interface CreateCreditPackageRequest {
  label: string;
  credits: number;
  price: number;
  isActive?: boolean;
  sortOrder?: number;
}

export type UpdateCreditPackageRequest = Partial<CreateCreditPackageRequest>;

// Promotion Packages
export type PromotionLevel = 'STANDARD' | 'FEATURED' | 'PREMIUM' | 'TOP' | 'WEEK' | 'MONTH';

export interface PromotionPackage {
  id: string;
  name: string;
  promotionLevel: PromotionLevel;
  creditCost: number;
  durationDays: number;
  isActive: boolean;
  sortOrder: number;
}

export interface CreatePromotionPackageRequest {
  name: string;
  promotionLevel: PromotionLevel;
  creditCost: number;
  durationDays: number;
  isActive?: boolean;
  sortOrder?: number;
}

export type UpdatePromotionPackageRequest = Partial<CreatePromotionPackageRequest>;

// Dealer Packages
export interface DealerPackage {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  features?: string[];
  maxListings?: number;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDealerPackageRequest {
  name: string;
  description?: string;
  price: number;
  currency?: string;
  features?: string[];
  maxListings?: number;
  isActive?: boolean;
  sortOrder?: number;
}

export type UpdateDealerPackageRequest = Partial<CreateDealerPackageRequest>;

// Credit Transactions
export type CreditTransactionType =
  | 'PURCHASE'
  | 'SUBSCRIPTION_GRANT'
  | 'AD_POST'
  | 'ADMIN_ADJUSTMENT'
  | 'REFUND';

export interface CreditTransactionListing {
  id: string;
  make?: string;
  model?: string;
  year?: number;
}

export interface CreditTransaction {
  id: string;
  userId: string;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  type: CreditTransactionType;
  reason: string;
  referenceId?: string;
  createdAt: string;
  user: { id: string; firstName: string; lastName: string; email: string };
  listing?: CreditTransactionListing | null;
}

export interface CreditTransactionFilters {
  userId?: string;
  type?: CreditTransactionType;
  startDate?: string;
  endDate?: string;
  listingId?: string;
}

export interface UserCreditSummary {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    currentBalance: number;
  };
  summary: {
    totalCreditsReceived: number;
    totalCreditsUsed: number;
    totalTransactions: number;
  };
  transactions: CreditTransaction[];
}

export interface CreditTimeSeriesPoint {
  period: string;
  creditsPurchased: number;
  creditsUsed: number;
}

export interface CreditAnomaly {
  userId: string;
  userName: string;
  userEmail: string;
  anomalyType: 'HIGH_DAILY_USAGE' | 'BUY_BURST' | 'LARGE_PURCHASE_OUTLIER';
  details: string;
  detectedAt: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH';
}

export interface CreditAnalytics {
  revenue: { total: number; currency: string; transactionCount: number };
  timeSeries: CreditTimeSeriesPoint[];
  topBuyers: Array<{
    rank: number;
    userId: string;
    name: string;
    email: string;
    totalCreditsPurchased: number;
  }>;
  anomalies: CreditAnomaly[];
}

// Payments
export type PaymentMethod = 'CARD' | 'CASH' | 'BANK_TRANSFER' | 'CREDITS';
export type PaymentStatus = 'PENDING' | 'COMPLETED' | 'FAILED' | 'REFUNDED';
export type PaymentType = 'LISTING_FEE' | 'CREDIT_PURCHASE' | 'SUBSCRIPTION';

export interface Payment {
  id: string;
  amount: number;
  currency: string;
  method: PaymentMethod;
  status: PaymentStatus;
  type: PaymentType;
  userId: string;
  listingId?: string;
  createdAt: string;
}

export interface PaymentStats {
  totalRevenue: number;
  totalPayments: number;
  pendingPayments: number;
  completedPayments: number;
  failedPayments: number;
  refundedPayments: number;
}

export const adminService = {
  // Dashboard Statistics
  getDashboardStats: async (): Promise<AdminStats> => {
    const { data } = await axiosInstance.get(
      API_ENDPOINTS.ADMIN.DASHBOARD_STATS
    );
    return data.success ? data.data : data;
  },

  // Recent Activity with Pagination
  getRecentActivity: async (
    page = 1,
    limit = 5
  ): Promise<PaginationResponse<RecentActivity>> => {
    const { data } = await axiosInstance.get(
      API_ENDPOINTS.ADMIN.RECENT_ACTIVITY,
      {
        params: { page, limit },
      }
    );
    return data.success ? data.data : data;
  },

  // Listing Management
  getListingDetails: async (
    listingId: string
  ): Promise<AdminListingDetails> => {
    const { data } = await axiosInstance.get(
      API_ENDPOINTS.ADMIN.LISTING_DETAILS(listingId)
    );
    return data.success ? data.data : data;
  },

  updateListingStatus: async (
    listingId: string,
    status: string,
    reason?: string,
    adminNotes?: string
  ): Promise<any> => {
    const { data } = await axiosInstance.patch(
      API_ENDPOINTS.ADMIN.UPDATE_LISTING_STATUS(listingId),
      {
        status,
        reason,
        adminNotes,
      }
    );
    return data;
  },

  // Search and Filtering
  // Search and Filtering
  searchListings: async (
    query: string = '',
    filters: any = {},
    page = 1,
    limit = 10
  ): Promise<PaginationResponse<any>> => {
    // ✅ Build params carefully to avoid sending empty/invalid ones
    const params: Record<string, any> = {
      page,
      limit,
    };

    // Only include non-empty query and filters
    if (query.trim()) params.q = query.trim();
    if (filters.status && filters.status !== 'all')
      params.status = filters.status;

    // Handle date range if exists
    if (filters.dateRange && filters.dateRange.length === 2) {
      const [start, end] = filters.dateRange;
      params.dateFrom = start;
      params.dateTo = end;
    }

    const { data } = await axiosInstance.get(
      API_ENDPOINTS.ADMIN.SEARCH_LISTINGS,
      { params }
    );

    // ✅ Always return structured data
    return data.success ? data.data : data;
  },

  // Admin Actions
  getAdminActions: async (
    page = 1,
    limit = 10
  ): Promise<PaginationResponse<any>> => {
    const { data } = await axiosInstance.get(
      API_ENDPOINTS.ADMIN.ADMIN_ACTIONS,
      {
        params: { page, limit },
      }
    );
    return data.data;
  },

  // Notifications
  sendNotification: async (userId: string, notification: any): Promise<any> => {
    const { data } = await axiosInstance.post(
      API_ENDPOINTS.ADMIN.SEND_NOTIFICATION,
      {
        userId,
        notification,
      }
    );
    return data;
  },

  // Edit Listing (admin override — no ownership check)
  adminUpdateListing: async (listingId: string, payload: {
    make?: string; model?: string; year?: number; price?: number; mileage?: number;
    color?: string; fuelType?: string; transmission?: string; bodyType?: string;
    condition?: string; description?: string; title?: string; isNegotiable?: boolean;
    location?: string; city?: string; country?: string; doors?: number; seats?: number;
    features?: string[]; status?: string; adminNotes?: string;
    contactPhone?: string; displayPhone?: boolean; showWhatsApp?: boolean;
    displacement?: string; driveType?: string; numberOfOwners?: number;
    interiorColor?: string; isMetallic?: string; interiorMaterial?: string;
  }): Promise<any> => {
    const { data } = await axiosInstance.patch(API_ENDPOINTS.ADMIN.EDIT_LISTING(listingId), payload);
    return data.success ? data.data : data;
  },

  // Delete Operations
  deleteListing: async (listingId: string): Promise<any> => {
    const { data } = await axiosInstance.delete(
      API_ENDPOINTS.ADMIN.DELETE_LISTING(listingId)
    );
    return data;
  },


  // Photo Management
  uploadListingPhotos: async (listingId: string, files: File[]): Promise<any> => {
    const form = new FormData();
    files.forEach(f => form.append('files', f));
    const { data } = await axiosInstance.post(
      API_ENDPOINTS.ADMIN.UPLOAD_LISTING_PHOTOS(listingId),
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  setPhotoPrimary: async (listingId: string, photoId: string): Promise<any> => {
    const { data } = await axiosInstance.patch(
      API_ENDPOINTS.ADMIN.SET_PRIMARY_PHOTO(listingId, photoId),
    );
    return data;
  },

  deletePhoto: async (listingId: string, photoId: string): Promise<any> => {
    const { data } = await axiosInstance.delete(
      API_ENDPOINTS.ADMIN.DELETE_PHOTO(listingId, photoId),
    );
    return data;
  },

  // Credit Packages
  getCreditPackages: async (): Promise<CreditPackage[]> => {
    const { data } = await axiosInstance.get(API_ENDPOINTS.ADMIN.CREDIT_PACKAGES);
    return data.success ? data.data : data;
  },

  createCreditPackage: async (payload: CreateCreditPackageRequest): Promise<CreditPackage> => {
    const { data } = await axiosInstance.post(API_ENDPOINTS.ADMIN.CREDIT_PACKAGES, payload);
    return data.success ? data.data : data;
  },

  updateCreditPackage: async (id: string, payload: UpdateCreditPackageRequest): Promise<CreditPackage> => {
    const { data } = await axiosInstance.patch(API_ENDPOINTS.ADMIN.CREDIT_PACKAGE_BY_ID(id), payload);
    return data.success ? data.data : data;
  },

  deleteCreditPackage: async (id: string): Promise<any> => {
    const { data } = await axiosInstance.delete(API_ENDPOINTS.ADMIN.CREDIT_PACKAGE_BY_ID(id));
    return data;
  },

  // Dealer Packages
  getDealerPackages: async (): Promise<DealerPackage[]> => {
    const { data } = await axiosInstance.get(API_ENDPOINTS.ADMIN.DEALER_PACKAGES);
    return Array.isArray(data) ? data : data.data ?? [];
  },

  createDealerPackage: async (payload: CreateDealerPackageRequest): Promise<DealerPackage> => {
    const { data } = await axiosInstance.post(API_ENDPOINTS.ADMIN.DEALER_PACKAGES, payload);
    return data.success ? data.data : data;
  },

  updateDealerPackage: async (id: string, payload: UpdateDealerPackageRequest): Promise<DealerPackage> => {
    const { data } = await axiosInstance.patch(API_ENDPOINTS.ADMIN.DEALER_PACKAGE_BY_ID(id), payload);
    return data.success ? data.data : data;
  },

  deleteDealerPackage: async (id: string): Promise<any> => {
    const { data } = await axiosInstance.delete(API_ENDPOINTS.ADMIN.DEALER_PACKAGE_BY_ID(id));
    return data;
  },

  // Promotion Packages
  getPromotionPackages: async (): Promise<PromotionPackage[]> => {
    const { data } = await axiosInstance.get(API_ENDPOINTS.ADMIN.PROMOTION_PACKAGES);
    return data.success ? data.data : data;
  },

  createPromotionPackage: async (payload: CreatePromotionPackageRequest): Promise<PromotionPackage> => {
    const { data } = await axiosInstance.post(API_ENDPOINTS.ADMIN.PROMOTION_PACKAGES, payload);
    return data.success ? data.data : data;
  },

  updatePromotionPackage: async (id: string, payload: UpdatePromotionPackageRequest): Promise<PromotionPackage> => {
    const { data } = await axiosInstance.patch(API_ENDPOINTS.ADMIN.PROMOTION_PACKAGE_BY_ID(id), payload);
    return data.success ? data.data : data;
  },

  deletePromotionPackage: async (id: string): Promise<any> => {
    const { data } = await axiosInstance.delete(API_ENDPOINTS.ADMIN.PROMOTION_PACKAGE_BY_ID(id));
    return data;
  },

  // Payments
  getPayments: async (
    page = 1,
    limit = 10,
    filters: { status?: PaymentStatus; type?: PaymentType; method?: PaymentMethod } = {}
  ): Promise<PaginationResponse<Payment>> => {
    const { data } = await axiosInstance.get(API_ENDPOINTS.PAYMENTS.GET_ALL, {
      params: { page, limit, ...filters },
    });
    return data.success ? data.data : data;
  },

  getPaymentStats: async (): Promise<PaymentStats> => {
    const { data } = await axiosInstance.get(API_ENDPOINTS.PAYMENTS.GET_STATS);
    return data.success ? data.data : data;
  },

  completePayment: async (id: string): Promise<any> => {
    const { data } = await axiosInstance.patch(API_ENDPOINTS.PAYMENTS.COMPLETE(id));
    return data;
  },

  failPayment: async (id: string): Promise<any> => {
    const { data } = await axiosInstance.patch(API_ENDPOINTS.PAYMENTS.FAIL(id));
    return data;
  },

  refundPayment: async (id: string): Promise<any> => {
    const { data } = await axiosInstance.patch(API_ENDPOINTS.PAYMENTS.REFUND(id));
    return data;
  },

  deletePayment: async (id: string): Promise<any> => {
    const { data } = await axiosInstance.delete(API_ENDPOINTS.PAYMENTS.DELETE(id));
    return data;
  },

  // Listing Approval Queue
  getListingApprovalQueue: async (
    page = 1,
    limit = 5,
    status = 'under_approval'
  ): Promise<any> => {
    const { data } = await axiosInstance.get('/admin/listings/approval-queue', {
      params: { page, limit, status },
    });
    return data.success ? data.data : data;
  },

  approveListing: async (
    id: string,
    payload: { action: string; adminNotes?: string; rejectionReason?: string }
  ): Promise<any> => {
    const { data } = await axiosInstance.patch(
      `/admin/listings/${id}/approve`,
      payload
    );
    return data;
  },

  // Token Verification Queue
  getTokenVerificationQueue: async (
    page = 1,
    limit = 5,
    status = 'token_verification_pending'
  ): Promise<any> => {
    const { data } = await axiosInstance.get(
      '/admin/listings/token-verification-queue',
      { params: { page, limit, status } }
    );
    return data.success ? data.data : data;
  },

  verifyToken: async (
    id: string,
    payload: { action: string; adminNotes?: string }
  ): Promise<any> => {
    const { data } = await axiosInstance.patch(
      `/admin/listings/${id}/verify-token`,
      payload
    );
    return data;
  },

  // Credit Transactions
  getCreditTransactions: async (
    filters: CreditTransactionFilters = {},
    page = 1,
    limit = 20
  ): Promise<{ transactions: CreditTransaction[]; pagination: PaginationResponse<CreditTransaction>['pagination'] }> => {
    const { data } = await axiosInstance.get(API_ENDPOINTS.ADMIN.CREDIT_TRANSACTIONS, {
      params: { ...filters, page, limit },
    });
    return data.success ? data.data : data;
  },

  getUserCreditSummary: async (userId: string): Promise<UserCreditSummary> => {
    const { data } = await axiosInstance.get(API_ENDPOINTS.ADMIN.USER_CREDIT_SUMMARY(userId));
    return data.success ? data.data : data;
  },

  getCreditAnalytics: async (params: {
    startDate?: string;
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month';
  } = {}): Promise<CreditAnalytics> => {
    const { data } = await axiosInstance.get(API_ENDPOINTS.ADMIN.CREDIT_ANALYTICS, {
      params,
    });
    return data.success ? data.data : data;
  },

  // Single user detail
  getUser: async (userId: string): Promise<AdminUserDetail> => {
    const { data } = await axiosInstance.get(API_ENDPOINTS.ADMIN.USER_BY_ID(userId));
    return data.success ? data.data : data;
  },

  // User's listings
  getUserListings: async (userId: string, page = 1, pageSize = 10): Promise<{
    total: number;
    page: number;
    pageSize: number;
    listings: AdminUserListing[];
  }> => {
    const { data } = await axiosInstance.get(API_ENDPOINTS.ADMIN.USER_LISTINGS(userId), {
      params: { page, pageSize },
    });
    return data.success ? data.data : data;
  },

  // Update user
  updateUser: async (userId: string, payload: UpdateUserPayload): Promise<AdminUserDetail> => {
    const { data } = await axiosInstance.patch(API_ENDPOINTS.ADMIN.UPDATE_USER(userId), payload);
    return data.success ? data.data : data;
  },

  // Delete user
  deleteUser: async (userId: string): Promise<void> => {
    await axiosInstance.delete(API_ENDPOINTS.ADMIN.DELETE_USER(userId));
  },

  // Manually adjust user credits
  adjustUserCredits: async (
    userId: string,
    amount: number,
    reason?: string,
  ): Promise<{ creditBalance: number }> => {
    const { data } = await axiosInstance.post(API_ENDPOINTS.ADMIN.ADJUST_CREDITS(userId), { amount, reason });
    return data.success ? data.data : data;
  },

  // Users list with filters and pagination
  listUsers: async (
    params: {
      role?: UserRole;
      status?: UserStatus;
      search?: string;
      page?: number;
      pageSize?: number;
    } = {}
  ): Promise<{
    page: number;
    pageSize: number;
    total: number;
    data: AdminUserItem[];
  }> => {
    const { role, status, search, page = 1, pageSize = 20 } = params;
    const query: Record<string, any> = { page, pageSize };
    if (role) query.role = role;
    if (status) query.status = status;
    if (search && search.trim()) query.search = search.trim();

    const { data } = await axiosInstance.get(API_ENDPOINTS.ADMIN.USERS, {
      params: query,
    });
    return data;
  },
};

export default adminService;
